import React, { useState, useEffect } from 'react';
import CustomCursor from './CustomCursor';
import Footer from './Footer';
import { Link, useLocation } from 'react-router-dom';
import { usePageContent } from '../../context/ContentContext';

const Layout = ({ children }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { g } = usePageContent('global');

  // `external` marks a destination outside this React app. The careers site is
  // a separate application served from /careers, so it needs a real page load —
  // a <Link> would be caught by the router and never leave the SPA.
  const navItems = [
    { name: g('header.navDesign'), path: '/design-development' },
    { name: g('header.navPortfolio'), path: '/portfolio' },
    { name: g('header.navMadeInLondon'), path: '/made-in-london' },
    { name: g('header.navOverseas'), path: '/overseas-production' },
    { name: g('header.navValues'), path: '/our-values' },
    { name: g('header.navContact'), path: '/contact' },
    { name: g('header.navCareers'), path: '/careers', external: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <div className="min-h-screen bg-pink-50">
      <CustomCursor />

      {/* Header */}
      <header className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md' : 'md:bg-transparent bg-white shadow-sm'
      }`}>
        <div className="container mx-auto px-4 py-2 lg:py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/">
                <img src={g('header.logo')} alt={g('header.logoAlt')} className="h-8 md:h-9 lg:h-12 xl:h-16" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center">
              {navItems.map((item, index) => {
                // Seven items now that Careers is here, so the gaps tighten on
                // narrower desktops to keep the nav clear of the logo.
                const className = `font-poppins font-semibold text-[11px] lg:text-sm xl:text-base transition-colors whitespace-nowrap ${
                  index !== navItems.length - 1 ? 'mr-3 lg:mr-4 xl:mr-6 2xl:mr-10' : ''
                } ${
                  location.pathname === item.path
                    ? 'text-primary-red'
                    : scrolled
                      ? 'text-gray-800 hover:text-primary-red'
                      : 'text-white hover:text-pink-200'
                }`;

                return item.external ? (
                  <a key={item.path} href={item.path} className={className}>
                    {item.name}
                  </a>
                ) : (
                  <Link key={item.path} to={item.path} className={className}>
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Mobile menu button (Burger) */}
            <button
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 relative z-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 transition-all duration-300 ${
                mobileMenuOpen
                  ? 'bg-gray-800 rotate-45 translate-y-1.5'
                  : scrolled ? 'bg-gray-800' : 'md:bg-white bg-gray-800'
              }`}></span>
              <span className={`block w-6 h-0.5 my-1 transition-all duration-300 ${
                mobileMenuOpen
                  ? 'opacity-0'
                  : scrolled ? 'bg-gray-800' : 'md:bg-white bg-gray-800'
              }`}></span>
              <span className={`block w-6 h-0.5 transition-all duration-300 ${
                mobileMenuOpen
                  ? 'bg-gray-800 -rotate-45 -translate-y-1.5'
                  : scrolled ? 'bg-gray-800' : 'md:bg-white bg-gray-800'
              }`}></span>
            </button>
          </div>
        </div>

      </header>

      {/* Mobile Navigation Overlay - outside header to avoid stacking context issues */}
      <div className={`md:hidden fixed inset-0 z-40 bg-white transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="pt-24 px-6">
          <nav className="flex flex-col space-y-4">
            {navItems.map((item) => {
              const className = `font-poppins font-semibold text-xl py-3 border-b border-gray-100 ${
                location.pathname === item.path ? 'text-primary-red' : 'text-gray-800'
              }`;

              return item.external ? (
                <a key={item.path} href={item.path} className={className}>
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={className}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <main className="pt-12 md:pt-0">
        {children}
      </main>

      <Footer />
    </div>
  );
};

export default Layout;
